import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Layers, Loader2, PackagePlus, Plus, RotateCcw, Save, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../lib/api";

/**
 * FT-3a §2 — the pending-changes bar. One Save, one revision batch, one summary. A project that
 * is past Open asks for a reason (and offers the change order) once for the whole batch, not per
 * cell — the same rule Operations applies, asked once.
 */
export default function FrsEditBar({ projectId, pending, postOpen, onSaved, onClose,
                                    onAddRoom, onAddProduct, selectedRooms = 0, onBulkSelection = null }) {
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [co, setCo] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [open, setOpen] = useState(true);

  const save = async () => {
    if (!pending.count) return;
    if (postOpen && !reason.trim()) {
      toast.error("This job is Open — say why the scope changed");
      return;
    }
    setBusy(true);
    setBlocked([]);
    try {
      const { data } = await api.post("/frs-grid/save", {
        project_id: projectId,
        ops: pending.ops,
        note: note.trim(),
        reason: reason.trim(),
        related_co_id: co.trim(),
        client_batch_id: `${projectId}-${Date.now()}-${pending.count}`,
      });
      pending.discard();
      setNote(""); setReason(""); setCo("");
      toast.success(data.replayed ? "Already saved" : "FRS saved", { description: data.summary });
      if (data.review_required) {
        toast.info("Flagged for FRS review", {
          description: "Operations will see the change on the job's review banner.",
        });
      }
      await onSaved?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 422 && d?.reason_required) {
        toast.error(d.detail || "A reason is required");
      } else if (d?.blocked) {
        setBlocked(d.blocked);
        toast.error(d.message || "Nothing was saved");
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Nothing was saved");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sticky top-0 z-[45] border-b border-amber-300 bg-amber-50" data-testid="frs-edit-bar">
      <div className="px-2 py-1.5 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900">
          Editing the FRS
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)}
                data-testid="frs-pending-count"
                className="h-7 px-2 rounded-full text-xs font-semibold border border-amber-400 bg-white text-amber-900">
          {pending.count} pending change{pending.count === 1 ? "" : "s"}
        </button>
        {onAddRoom && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddRoom}
                  data-testid="frs-add-room-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Room
          </Button>
        )}
        {onAddProduct && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddProduct}
                  data-testid="frs-add-product-btn">
            <PackagePlus className="w-3.5 h-3.5 mr-1" /> Product
          </Button>
        )}
        {onBulkSelection && selectedRooms > 0 && (
          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-400 bg-white text-amber-900" onClick={onBulkSelection}
                  data-testid="frs-bulk-selection-btn" title="One quantity or room type across every ticked room — previewed, then staged for the same Save">
            <Layers className="w-3.5 h-3.5 mr-1" /> Bulk edit {selectedRooms} room{selectedRooms === 1 ? "" : "s"}
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!pending.canUndo}
                onClick={() => { if (pending.undo()) toast.info("Undid last change"); }}
                data-testid="frs-undo-btn" title="Undo the last change">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Undo
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-700" disabled={busy}
                  onClick={() => { pending.discard(); setBlocked([]); toast.info("Pending changes discarded"); }}
                  data-testid="frs-discard-btn">
            <Undo2 className="w-3.5 h-3.5 mr-1" /> Discard
          </Button>
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy || !pending.count} onClick={save} data-testid="frs-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save {pending.count || ""}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose}
                  data-testid="frs-edit-close-btn" title="Leave edit mode">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {open && pending.count > 0 && (
        <div className="px-2 pb-2 grid gap-2 lg:grid-cols-[1fr_320px]" data-testid="frs-pending-panel">
          <div className="max-h-40 overflow-y-auto rounded-md border border-amber-200 bg-white p-2 text-xs">
            {pending.summary.map(({ room, lines }) => (
              <div key={room} className="mb-1 last:mb-0" data-testid={`frs-pending-room-${room}`}>
                <b className="text-gray-900">{room}:</b>{" "}
                <span className="text-gray-700">{lines.join("; ")}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            {postOpen && (
              <>
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="Why did the scope change? (required — this job is Open)"
                       className="h-8 text-xs" data-testid="frs-reason-input" />
                <Input value={co} onChange={(e) => setCo(e.target.value)}
                       placeholder="Change order (optional)"
                       className="h-8 text-xs" data-testid="frs-co-input" />
              </>
            )}
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      placeholder="Note for the revision log (optional)"
                      className="text-xs" data-testid="frs-note-input" />
          </div>
        </div>
      )}

      {blocked.length > 0 && (
        <div className="px-2 pb-2" data-testid="frs-blocked-panel">
          <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
            <div className="flex items-center gap-1 font-semibold">
              <AlertTriangle className="w-3.5 h-3.5" /> Nothing was saved
            </div>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {blocked.slice(0, 8).map((b, i) => <li key={i}>{b.message}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
